import { useMemo } from 'react';

import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { mapStateSlugToListingsTableSaleType } from 'Stores/AdditionalVehicleDetailsStore/AdditionalVehicleDetailsStore.helpers';
import { ForSaleCategory, UnsoldCategory } from 'Types/enquiryStates';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import { useGetAdditionalVehicleDetails } from 'Utilities/hooks/vehicle/useGetAdditionalVehicleDetails';
import { useIsBinVehicleProcessing } from 'Utilities/hooks/vehicle/useIsBinVehicleProcessing';
import { notYetForSaleVehicleStates, soldStates } from 'Utilities/vehicleState';

export type UseBiddingViewName =
	| 'saleActive'
	| 'saleOverProcessing'
	| 'underOffer'
	| 'buyItNow'
	| 'sold'
	| 'notYetForSale'
	| 'unsold'
	| null;

export const useBiddingViewName = (): UseBiddingViewName => {
	const { id, state } = useVehicleData();
	const { slug } = state;
	const saleType = mapStateSlugToListingsTableSaleType(slug);
	const { expiryDate } = useGetAdditionalVehicleDetails({ id, saleType }) ?? {};
	const { isLiveSaleEnabled, isSaleActive } = useSaleTimes();
	const isLiveSaleVehicleProcessing = slug === ForSaleCategory.saleActive && !isSaleActive;
	const isLiveSaleVehicleInDisabledSale = slug === ForSaleCategory.saleActive && isSaleActive && !isLiveSaleEnabled;
	const isBinVehicleProcessing = useIsBinVehicleProcessing({ expiryDate, slug });

	return useMemo(() => {
		switch (true) {
			case slug === ForSaleCategory.saleActive && isSaleActive && isLiveSaleEnabled:
				return 'saleActive';
			case slug === ForSaleCategory.underOffer || slug === UnsoldCategory.unsoldBelowReserveBid:
				return 'underOffer';
			case soldStates.some((stateSlug) => stateSlug === slug):
				return 'sold';
			case notYetForSaleVehicleStates.some((stateSlug) => stateSlug === slug):
				return 'notYetForSale';
			case isLiveSaleVehicleProcessing || isLiveSaleVehicleInDisabledSale || isBinVehicleProcessing:
				return 'saleOverProcessing';
			case slug === ForSaleCategory.forSale:
				return 'buyItNow';
			case slug === UnsoldCategory.unsoldNoBids:
				return 'unsold';
			default:
				return null;
		}
	}, [
		slug,
		isSaleActive,
		isBinVehicleProcessing,
		isLiveSaleVehicleProcessing,
		isLiveSaleVehicleInDisabledSale,
		isLiveSaleEnabled,
	]);
};
